import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Col, Row } from '@/lib/utils';
import { colors, semanticColors } from '@/lib/colors';
import { LockerStatusData, LockerStatusResponse } from '@/features/locker/types';
import {
    invalidateLockerRelatedQueries,
    useReleaseExpiredLockers,
    useReleaseLocker,
    useSetLockerSlot,
} from '@/features/locker/hooks';
import {
    LOCKER_STATUS_FILTERS,
    LockerStatusFilter,
    buildSlotUpdateBody,
    getBulkInitialValue,
    matchesLockerFilter,
    matchesLockerSearch,
} from '@/features/locker/lockerDisplay';
import { getMutationErrorMessage } from '@/features/locker/mutationError';
import { BulkCellState, LockerBulkCell, LockerCard, LockerCardStyles } from './LockerCard';
import { LockerEmpty, LockerError, LockerLoading } from './LockerStates';
import LockerAssignModal from './LockerAssignModal';
import LockerSlotPasswordModal from './LockerSlotPasswordModal';

const ITEMS_PER_PAGE = 100;
const SEARCH_DEBOUNCE_MS = 350;

const toolbarButtonStyle = (active?: boolean): React.CSSProperties => ({
    height: 36,
    padding: '0 14px',
    borderRadius: 8,
    border: `1px solid ${active ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
    backgroundColor: active ? colors.BLUE_50 : colors.white,
    color: active ? colors.PRIMARY_BLUE : colors.LAVEL_1,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
});

export default function LockerAssignmentTab({
    centerId,
    zoneId,
    statusData,
    isLoading,
    isError,
    onRetry,
    readOnly,
    revealAll,
    onToggleReveal,
    onDirtyChange,
    saveChainsRef,
    createAlert,
    createTwoButtonAlert,
}: {
    centerId: string;
    zoneId: string | null;
    statusData?: LockerStatusResponse | null;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    readOnly: boolean;
    revealAll: boolean;
    onToggleReveal: () => void;
    /** 일괄 입력에 저장 대기·실패가 남아 있는지 페이지에 알린다(탭 전환 차단용). */
    onDirtyChange?: (dirty: boolean) => void;
    /** 칸별 저장 큐. 탭 언마운트를 넘어 살아남아야 해서 **페이지가 소유한다**. */
    saveChainsRef?: React.MutableRefObject<Record<number, Promise<void>>>;
    createAlert?: any;
    createTwoButtonAlert?: any;
}) {
    const navigate = useNavigate();
    const qc = useQueryClient();

    const releaseMutation = useReleaseLocker();
    const releaseExpiredMutation = useReleaseExpiredLockers();
    // 일괄 모드는 셀마다 invalidate 하지 않는다. 모드를 끝낼 때 한 번만 버린다.
    const bulkSlotMutation = useSetLockerSlot({ autoInvalidate: false });

    /*
     * useMutation 이 돌려주는 객체는 **렌더마다 새 객체**다. 그대로 useCallback 의 의존성에 넣으면
     * 저장이 시작·종료될 때마다 핸들러 신원이 바뀌고, 그 핸들러를 prop 으로 받는 카드 100장이
     * 전부 다시 그려진다 — memo 를 붙인 의미가 사라진다. 호출 함수만 ref 로 고정한다.
     */
    const releaseRef = useRef(releaseMutation.mutate);
    releaseRef.current = releaseMutation.mutate;
    /*
     * createAlert / createTwoButtonAlert 도 **App.tsx 가 매 렌더 새로 만든다.**
     * 의존성에 그대로 넣으면 App 이 다시 그려질 때마다 핸들러 신원이 바뀌어 카드 100장이
     * 함께 다시 그려진다 — mutation 만 고정하고 알럿을 놔두면 memo 가 반만 걸린다.
     */
    const alertRef = useRef(createAlert);
    alertRef.current = createAlert;
    const twoButtonAlertRef = useRef(createTwoButtonAlert);
    twoButtonAlertRef.current = createTwoButtonAlert;
    const bulkSaveRef = useRef(bulkSlotMutation.mutateAsync);
    bulkSaveRef.current = bulkSlotMutation.mutateAsync;

    const lockers = useMemo(() => statusData?.lockers ?? [], [statusData]);

    // ─────────────────────────────── 검색 · 필터 ───────────────────────────────
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<LockerStatusFilter>('all');
    const [page, setPage] = useState(1);

    // 레포 관례대로 useEffect + setTimeout 인라인 디바운스.
    useEffect(() => {
        const timer = window.setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter]);

    const filtered = useMemo(
        () => lockers.filter((locker) => matchesLockerFilter(locker, statusFilter) && matchesLockerSearch(locker, search)),
        [lockers, statusFilter, search],
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const pageItems = useMemo(
        () => filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE),
        [filtered, safePage],
    );

    // ─────────────────────────────── 선택 · 모달 ───────────────────────────────
    const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
    const [assignModalNumber, setAssignModalNumber] = useState<number | null>(null);
    const [passwordModalNumber, setPasswordModalNumber] = useState<number | null>(null);

    const selectedLocker = useMemo(
        () => (selectedNumber === null ? null : lockers.find((locker) => locker.number === selectedNumber) ?? null),
        [lockers, selectedNumber],
    );

    // ─────────────────────────────── 일괄 입력 ───────────────────────────────
    const [bulkMode, setBulkMode] = useState(false);
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [cellStates, setCellStates] = useState<Record<number, BulkCellState>>({});
    /** 셀별 실패 사유. 버리면 32자 초과 같은 영구 실패를 이유도 모르고 재시도하게 된다. */
    const [cellErrors, setCellErrors] = useState<Record<number, string>>({});
    const [activeBulkNumber, setActiveBulkNumber] = useState<number | null>(null);
    const [focusedBulkNumber, setFocusedBulkNumber] = useState<number | null>(null);

    /**
     * 칸별 저장 큐 — **같은 칸**의 요청만 직렬화한다.
     * 같은 칸에 두 번 저장하면 늦게 도착한 옛 요청이 최신 값을 덮을 수 있다.
     *
     * ⚠️ **모드를 끝내도, 이 탭이 언마운트돼도 비우지 않는다.** 이미 전송된 PUT 은 취소되지
     * 않고 슬롯 쓰기는 버전 조건 없는 upsert 라, 큐가 사라지면 옛 PUT 과 새 PUT 이 다시
     * 병렬이 되어 늦게 도착한 옛 값이 이긴다. 그래서 **페이지가 소유한 ref** 를 받아 쓴다
     * (탭 전환 = 이 컴포넌트의 언마운트다). prop 이 없으면 로컬 ref 로 떨어진다.
     * 세대 토큰은 UI 상태 반영만 막는다 — 서버 쓰기 순서는 이 체인이 책임진다.
     */
    const localChainsRef = useRef<Record<number, Promise<void>>>({});
    const slotChainsRef = saveChainsRef ?? localChainsRef;
    /** 이번 마운트에서 저장을 한 번이라도 시도했는가. 언마운트 시 invalidate 여부를 정한다. */
    const bulkTouchedRef = useRef(false);
    /** 저장에 성공한 값. dirty 판정의 기준선이라 저장할 때마다 갱신한다. */
    const baselineRef = useRef<Record<number, string>>({});
    /** 이미 큐에 넣은 값. Enter 로 커밋한 직후 blur 가 같은 값을 또 보내는 것을 막는다. */
    const committedRef = useRef<Record<number, string>>({});
    /** 입력값의 최신본. 상태로 읽으면 커밋 핸들러가 매 타이핑마다 새로 만들어져 셀 100장이 다시 그려진다. */
    const draftsRef = useRef<Record<number, string>>({});

    /**
     * 모드 세대(generation).
     *
     * 모드를 끝내면 올린다. 종료 뒤에 도착한 옛 응답이 새 세대의 상태를 건드리거나
     * 최신 값을 덮지 못하게 하는 유일한 장치다.
     */
    const generationRef = useRef(0);

    /**
     * 칸 **사이** 동시 요청 4개 제한.
     *
     * 칸별 직렬화만으로는 100칸을 빠르게 입력했을 때 요청 100개가 한꺼번에 나간다
     * (ky retry:0, timeout 20초). 서버와 브라우저 커넥션 풀을 동시에 때린다.
     */
    const BULK_CONCURRENCY = 4;
    const runningRef = useRef(0);
    const waitersRef = useRef<(() => void)[]>([]);

    const acquireSlot = useCallback(async () => {
        if (runningRef.current < BULK_CONCURRENCY) {
            runningRef.current += 1;
            return;
        }
        await new Promise<void>((resolve) => waitersRef.current.push(resolve));
        runningRef.current += 1;
    }, []);

    const releaseSlot = useCallback(() => {
        runningRef.current = Math.max(0, runningRef.current - 1);
        const next = waitersRef.current.shift();
        if (next) next();
    }, []);

    const baselineOf = useCallback((locker: LockerStatusData) => {
        const saved = baselineRef.current[locker.number];
        return saved !== undefined ? saved : getBulkInitialValue(locker);
    }, []);

    const setCellState = useCallback((lockerNumber: number, state: BulkCellState) => {
        setCellStates((prev) => (prev[lockerNumber] === state ? prev : { ...prev, [lockerNumber]: state }));
    }, []);

    const pendingCount = useMemo(
        () => Object.values(cellStates).filter((state) => state === 'dirty' || state === 'saving').length,
        [cellStates],
    );
    const failedCount = useMemo(() => Object.values(cellStates).filter((state) => state === 'failed').length, [cellStates]);
    const savedCount = useMemo(() => Object.values(cellStates).filter((state) => state === 'saved').length, [cellStates]);
    const hasUnfinishedBulkWork = bulkMode && (pendingCount > 0 || failedCount > 0);

    // 저장 대기·실패가 남은 채로 창을 닫으면 입력이 사라진다.
    // (앱 안에서의 탭 전환은 이걸로 못 막는다 — onDirtyChange 로 페이지가 막는다.)
    useEffect(() => {
        if (!hasUnfinishedBulkWork) return undefined;
        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnfinishedBulkWork]);

    const onDirtyChangeRef = useRef(onDirtyChange);
    onDirtyChangeRef.current = onDirtyChange;

    useEffect(() => {
        onDirtyChangeRef.current?.(hasUnfinishedBulkWork);
    }, [hasUnfinishedBulkWork]);

    /**
     * 언마운트 정리 — **탭 전환이 곧 언마운트**다.
     *
     * 전부 성공하면 경고가 없어 종료 절차 없이 사라지는데, 그러면
     *   ① invalidate 가 안 돌아 staleTime 1분 동안 방금 저장한 고정값이 안 보인다.
     *      관리자는 입력이 날아간 줄 알고 다시 친다.
     *   ② onDirtyChange 가 true 로 남아 엉뚱한 경고가 뜬다.
     * 세대를 올려 남은 응답이 상태를 건드리지 못하게 하고, in-flight 를 기다린 뒤 버린다.
     */
    useEffect(() => {
        const chainsRef = slotChainsRef;
        return () => {
            onDirtyChangeRef.current?.(false);
            generationRef.current += 1;
            if (!bulkTouchedRef.current) return;
            void Promise.allSettled(Object.values(chainsRef.current)).then(() =>
                invalidateLockerRelatedQueries(qc),
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qc]);

    const runSave = useCallback(
        async (lockerNumber: number, value: string, generation: number) => {
            if (!zoneId) return;
            if (generation !== generationRef.current) return;
            setCellState(lockerNumber, 'saving');
            await acquireSlot();
            try {
                // 대기하는 사이에 모드가 끝났으면 보내지 않는다.
                if (generation !== generationRef.current) return;
                await bulkSaveRef.current({
                    centerId,
                    zoneId,
                    lockerNumber,
                    // 일괄 입력이 다루는 값은 **칸의 고정값뿐**이다(getBulkInitialValue 참조).
                    body: buildSlotUpdateBody({ defaultLockNumber: value }),
                });
                // 서버에는 반영됐다. 세대가 바뀌었어도 baseline 은 사실이므로 남긴다.
                baselineRef.current[lockerNumber] = value.trim();
                if (generation !== generationRef.current) return;
                setCellErrors((prev) => {
                    if (!prev[lockerNumber]) return prev;
                    const next = { ...prev };
                    delete next[lockerNumber];
                    return next;
                });
                /*
                 * 저장하는 동안 **더 최신 입력**이 들어왔을 수 있다.
                 * 그때 'saved' 로 바꾸면 미저장 경고가 사라져, 관리자는 저장된 줄 알고 나간다.
                 * 응답이 대상으로 한 값과 지금 화면의 값이 다르면 dirty 를 유지한다.
                 */
                const latest = draftsRef.current[lockerNumber];
                if (latest !== undefined && latest.trim() !== value.trim()) {
                    setCellState(lockerNumber, 'dirty');
                    return;
                }
                setCellState(lockerNumber, 'saved');
            } catch (error) {
                /*
                 * 큐 투입 기록을 **반드시 푼다.**
                 *
                 * 안 풀면 같은 값으로 다시 저장하려 할 때 "이미 커밋됨"으로 건너뛴다.
                 * 화면에는 "실패 · 다시 저장"이라고 쓰여 있는데 아무 일도 일어나지 않는다.
                 * 세대가 바뀌어도 푼다 — 재진입 후의 재시도까지 막으면 안 된다.
                 */
                if (committedRef.current[lockerNumber] === value) delete committedRef.current[lockerNumber];
                if (generation !== generationRef.current) return;
                /*
                 * 실패한 셀만 표시가 남는다. 입력값은 draft 에 그대로 있어 다시 저장할 수 있다.
                 * 캐시를 낙관적으로 건드리지 않으므로 되돌릴 배열 스냅샷 자체가 없다.
                 * 사유를 버리면 32자 초과 같은 **영구 실패**를 계속 재시도하게 된다.
                 */
                setCellErrors((prev) => ({
                    ...prev,
                    [lockerNumber]: getMutationErrorMessage(error, '저장에 실패했습니다.'),
                }));
                setCellState(lockerNumber, 'failed');
            } finally {
                releaseSlot();
            }
        },
        [acquireSlot, centerId, releaseSlot, setCellState, zoneId],
    );

    const enqueueSave = useCallback(
        (lockerNumber: number, value: string) => {
            const generation = generationRef.current;
            committedRef.current[lockerNumber] = value;
            bulkTouchedRef.current = true;
            const previous = slotChainsRef.current[lockerNumber] ?? Promise.resolve();
            /*
             * `.catch` 가 없으면 앞 요청이 한 번 reject 되는 순간 이후 `.then` 이 runSave 를
             * **영영 호출하지 않는다.** 체인을 비우지 않는 설계라 그 칸이 통째로 죽는다.
             */
            const next = previous.catch(() => {}).then(() => runSave(lockerNumber, value, generation));
            slotChainsRef.current[lockerNumber] = next;
        },
        [runSave, slotChainsRef],
    );

    const handleBulkChange = useCallback(
        (lockerNumber: number, value: string) => {
            draftsRef.current[lockerNumber] = value;
            setDrafts((prev) => ({ ...prev, [lockerNumber]: value }));
            const locker = lockers.find((item) => item.number === lockerNumber);
            if (!locker) return;
            const baseline = baselineOf(locker);
            setCellState(lockerNumber, value.trim() === baseline.trim() ? 'idle' : 'dirty');
        },
        [baselineOf, lockers, setCellState],
    );

    const handleBulkCommit = useCallback(
        (lockerNumber: number) => {
            const locker = lockers.find((item) => item.number === lockerNumber);
            if (!locker) return;
            const draft = draftsRef.current[lockerNumber];
            if (draft === undefined) return;

            /*
             * 이미 같은 값을 큐에 넣었으면 건너뛴다.
             *
             * Enter 는 커밋한 뒤 다음 셀로 포커스를 옮기고, 그 순간 앞 셀에서 blur 가 나
             * 커밋이 한 번 더 돈다. baseline 은 **성공한 뒤**에야 갱신되므로 그것만으로는
             * 못 막아 변경 한 건에 PUT 이 두 번 나갔다.
             */
            if (committedRef.current[lockerNumber] === draft) return;

            const baseline = baselineOf(locker);
            // 값이 그대로면 저장하지 않는다. 단순 이동으로 요청이 나가면 안 된다.
            if (draft.trim() === baseline.trim()) {
                setCellState(lockerNumber, 'idle');
                return;
            }

            /*
             * **비우기는 확인을 받는다.**
             *
             * 빈 값은 `defaultLockNumber: null` 로 나가 서버가 슬롯 문서를 접는다. 되돌릴 수
             * 없는데 셀은 마스킹돼 있어 지운 값이 화면에도 남지 않는다. 같은 파괴를 단건
             * 모달은 확인받으므로 문구와 기준을 그쪽에 맞춘다.
             */
            if (draft.trim() === '' && baseline.trim() !== '') {
                const confirmAlert = twoButtonAlertRef.current;
                // 확인 수단이 없으면 **실행하지 않는다.** 조용히 지우는 쪽이 더 나쁘다.
                if (!confirmAlert) {
                    setCellState(lockerNumber, 'dirty');
                    return;
                }
                confirmAlert(
                    `${lockerNumber}번 칸의 고정 비밀번호를 해제할까요?`,
                    '이 칸의 고정 비밀번호가 사라집니다. 개별 비밀번호가 없는 회원은 바로 비밀번호 없음 상태가 됩니다.',
                    true,
                    '취소',
                    '해제하기',
                    () => setCellState(lockerNumber, 'dirty'),
                    () => enqueueSave(lockerNumber, draft),
                );
                return;
            }

            enqueueSave(lockerNumber, draft);
        },
        [baselineOf, enqueueSave, lockers, setCellState],
    );

    const handleBulkFocus = useCallback((lockerNumber: number) => {
        setActiveBulkNumber(lockerNumber);
        setFocusedBulkNumber(lockerNumber);
    }, []);

    const handleBulkBlur = useCallback(
        (lockerNumber: number) => {
            setFocusedBulkNumber((prev) => (prev === lockerNumber ? null : prev));
            handleBulkCommit(lockerNumber);
        },
        [handleBulkCommit],
    );

    const pageNumbersRef = useRef<number[]>([]);
    pageNumbersRef.current = pageItems.map((locker) => locker.number);

    const handleBulkMove = useCallback((fromNumber: number, delta: number) => {
        const numbers = pageNumbersRef.current;
        const index = numbers.indexOf(fromNumber);
        if (index === -1) return;
        const target = numbers[index + delta];
        if (target === undefined) return;
        // memo 로 감싼 셀에 ref 를 꿰는 대신 id 로 찾는다. 셀은 페이지당 100개뿐이다.
        document.getElementById(`locker-bulk-${target}`)?.focus();
    }, []);

    /*
     * 페이지나 필터가 바뀌면 활성 셀을 놓는다. 그러지 않으면 활성 번호가 현재 페이지에 없어
     * 모든 셀이 tabIndex=-1 이 되고 Tab 으로 그리드에 들어갈 수 없다.
     */
    useEffect(() => {
        setActiveBulkNumber(null);
    }, [safePage, statusFilter, search]);

    const finishBulkMode = useCallback(() => {
        // 세대를 먼저 올린다. 이 뒤에 도착하는 응답은 전부 무시된다.
        const finishedGeneration = generationRef.current;
        generationRef.current += 1;
        const chains = Object.values(slotChainsRef.current);

        setBulkMode(false);
        setDrafts({});
        setCellStates({});
        setCellErrors({});
        setActiveBulkNumber(null);
        setFocusedBulkNumber(null);
        // slotChainsRef 는 **비우지 않는다**(위 주석 참조). 재진입 요청이 옛 요청 뒤에 서야 한다.
        baselineRef.current = {};
        draftsRef.current = {};
        committedRef.current = {};

        /*
         * 모드 중에는 한 번도 invalidate 하지 않았다. 여기서 한 번만 최신 상태를 받아 온다 —
         * 단 **큐가 다 끝난 뒤**다. 진행 중에 버리면 저장 전 값을 다시 받아 온다.
         */
        void Promise.allSettled(chains).then(() => {
            if (generationRef.current !== finishedGeneration + 1) return;
            invalidateLockerRelatedQueries(qc);
        });
    }, [qc, slotChainsRef]);

    const exitBulkMode = useCallback(() => {
        const summary = `${savedCount}칸 저장${failedCount > 0 ? `, ${failedCount}칸 실패` : ''}`;
        if (pendingCount > 0 || failedCount > 0) {
            const confirmAlert = twoButtonAlertRef.current;
            // 확인 수단이 없으면 나가지 않는다. 말없이 입력을 버리는 쪽이 더 나쁘다.
            if (!confirmAlert) return;
            confirmAlert(
                '아직 저장되지 않은 칸이 있습니다',
                /*
                 * "전부 사라진다"고 하면 거짓이다. finishBulkMode 는 **아직 시작 안 한**
                 * 저장만 취소하고, 이미 전송된 것(최대 4건)은 그대로 서버에 저장된다.
                 * 파괴 확인 문구가 거짓이면 확인 자체의 신뢰가 깎인다.
                 */
                `저장 대기 ${pendingCount}칸, 실패 ${failedCount}칸이 남아 있습니다. 지금 나가면 아직 보내지 않은 입력은 사라지고, 이미 전송 중인 일부는 그대로 저장될 수 있습니다.`,
                true,
                '계속 입력',
                '나가기',
                () => {},
                () => {
                    finishBulkMode();
                    alertRef.current?.('일괄 입력 종료', summary, failedCount > 0);
                },
            );
            return;
        }
        finishBulkMode();
        if (savedCount > 0) alertRef.current?.('일괄 입력 종료', summary, false);
    }, [failedCount, finishBulkMode, pendingCount, savedCount]);

    const enterBulkMode = useCallback(() => {
        setSelectedNumber(null);
        setBulkMode(true);
        setActiveBulkNumber(null);
    }, []);

    // ─────────────────────────────── 액션 ───────────────────────────────
    const calculateDday = useCallback((endDate: string) => {
        const today = dayjs().startOf('day');
        return dayjs(endDate).startOf('day').diff(today, 'day');
    }, []);

    const handleToggleSelect = useCallback((lockerNumber: number) => {
        setSelectedNumber((prev) => (prev === lockerNumber ? null : lockerNumber));
    }, []);

    const handleCloseMenu = useCallback(() => setSelectedNumber(null), []);

    const handleAssign = useCallback((lockerNumber: number) => {
        setSelectedNumber(null);
        setAssignModalNumber(lockerNumber);
    }, []);

    const handleEditPassword = useCallback((lockerNumber: number) => {
        setSelectedNumber(null);
        setPasswordModalNumber(lockerNumber);
    }, []);

    /**
     * 모달을 닫으면 그것을 연 카드로 포커스를 되돌린다.
     *
     * 그러지 않으면 포커스가 body 로 떨어져, 키보드 사용자는 방금 어디에 있었는지 잃고
     * 그리드 처음부터 다시 훑어야 한다.
     */
    const closeModalAndRefocus = useCallback((lockerNumber: number, close: () => void) => {
        close();
        requestAnimationFrame(() => document.getElementById(`locker-card-${lockerNumber}`)?.focus());
    }, []);

    const handleOpenMember = useCallback((memberId: string) => navigate(`/member/${memberId}`), [navigate]);

    const runRelease = useCallback(
        (locker: LockerStatusData) => {
            if (!locker.subscription?._id) return;
            releaseRef.current(
                { centerId, subscriptionId: locker.subscription._id },
                {
                    onSuccess: () => {
                        setSelectedNumber(null);
                        alertRef.current?.('알림', '라커가 회수되었습니다.', false);
                    },
                    onError: (error) =>
                        alertRef.current?.('오류', getMutationErrorMessage(error, '라커 회수에 실패했습니다.'), true),
                },
            );
        },
        [centerId],
    );

    /*
     * 단건 회수도 확인을 받는다.
     *
     * 메뉴 항목이라 만료 일괄 회수보다 오히려 누르기 쉬운데, 지금까지는 누르는 즉시 실행됐다.
     * 회수하면 그 회원의 라커 배정과 비밀번호가 함께 풀린다.
     */
    const handleRelease = useCallback(
        (locker: LockerStatusData) => {
            if (!locker.subscription?._id) return;
            const confirmAlert = twoButtonAlertRef.current;
            // 확인 수단이 없으면 회수하지 않는다. 확인 없는 파괴 경로를 남기지 않는다.
            if (!confirmAlert) return;
            const memberName = locker.subscription.memberName || '이 회원';
            confirmAlert(
                `${locker.number}번 라커를 회수할까요?`,
                `${memberName} 님의 배정이 풀리고 이 번호는 다시 배정할 수 있게 됩니다.`,
                true,
                '취소',
                '회수하기',
                () => {},
                () => runRelease(locker),
            );
        },
        [runRelease],
    );

    const assignedCount = statusData?.assignedCount ?? lockers.filter((l) => l.status === 'ASSIGNED').length;
    const pausedCount = statusData?.pausedCount ?? lockers.filter((l) => l.status === 'PAUSED').length;
    const expiredCount = statusData?.expiredCount ?? lockers.filter((l) => l.status === 'EXPIRED').length;

    const runReleaseExpired = useCallback(() => {
        if (!zoneId || expiredCount === 0) return;
        releaseExpiredMutation.mutate(
            { centerId, zoneId },
            {
                onSuccess: (res) => {
                    setSelectedNumber(null);
                    alertRef.current?.('알림', `만료된 라커 ${res.data?.modifiedCount ?? 0}개를 회수했습니다.`, false);
                },
                onError: (error) =>
                    alertRef.current?.('오류', getMutationErrorMessage(error, '만료된 라커 일괄 회수에 실패했습니다.'), true),
            },
        );
    }, [centerId, expiredCount, releaseExpiredMutation, zoneId]);

    const handleReleaseExpired = useCallback(() => {
        if (!zoneId || expiredCount === 0) return;
        const confirmAlert = twoButtonAlertRef.current;
        // 확인 수단이 없으면 일괄 회수하지 않는다.
        if (!confirmAlert) return;
        confirmAlert(
            '만료된 라커를 모두 회수할까요?',
            `만료 라커 ${expiredCount}개를 회수합니다. 회수 후 해당 번호는 다시 배정할 수 있습니다.`,
            true,
            '취소',
            '모두 회수',
            () => {},
            runReleaseExpired,
        );
    }, [expiredCount, runReleaseExpired, zoneId]);

    // ─────────────────────────────── 렌더 ───────────────────────────────
    if (isLoading) return <LockerLoading />;
    if (isError) return <LockerError onRetry={onRetry} />;

    if (lockers.length === 0) {
        return (
            <LockerEmpty
                title="아직 라커 칸이 없습니다"
                description="센터에 라커 구역이 만들어지면 칸이 이곳에 표시됩니다. 잠시 후에도 비어 있으면 새로고침해 주세요."
                actionLabel="다시 불러오기"
                onAction={onRetry}
            />
        );
    }

    const hasSearchOrFilter = search.trim() !== '' || statusFilter !== 'all';
    const passwordModalLocker = passwordModalNumber;

    return (
        <Col style={{ gap: 16 }}>
            <LockerCardStyles />

            {/* 요약 + 도구 */}
            <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Row style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: colors.LAVEL_1 }}>
                        배정 <b style={{ color: colors.LAVEL_4 }}>{assignedCount}</b>
                        {pausedCount > 0 && (
                            <>
                                {' · '}
                                정지 <b style={{ color: semanticColors.warning.text }}>{pausedCount}</b>
                            </>
                        )}
                        {expiredCount > 0 && (
                            <>
                                {' · '}
                                만료 <b style={{ color: semanticColors.danger.text }}>{expiredCount}</b>
                            </>
                        )}
                        {' / '}
                        전체 {lockers.length}
                    </span>
                </Row>

                <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={onToggleReveal}
                        aria-pressed={revealAll}
                        style={toolbarButtonStyle(revealAll)}
                    >
                        {revealAll ? '비밀번호 가리기' : '비밀번호 표시'}
                    </button>
                    {!readOnly && !bulkMode && (
                        <button type="button" onClick={enterBulkMode} style={toolbarButtonStyle()}>
                            일괄 입력
                        </button>
                    )}
                    {!readOnly && bulkMode && (
                        <button type="button" onClick={exitBulkMode} style={toolbarButtonStyle(true)}>
                            일괄 입력 종료
                        </button>
                    )}
                    {!readOnly && !bulkMode && expiredCount > 0 && (
                        <button
                            type="button"
                            onClick={handleReleaseExpired}
                            disabled={releaseExpiredMutation.isLoading}
                            style={{
                                ...toolbarButtonStyle(),
                                border: `1px solid ${semanticColors.danger.border}`,
                                backgroundColor: semanticColors.danger.bg,
                                color: semanticColors.danger.text,
                                cursor: releaseExpiredMutation.isLoading ? 'not-allowed' : 'pointer',
                                opacity: releaseExpiredMutation.isLoading ? 0.6 : 1,
                            }}
                        >
                            만료된 라커 모두 회수
                        </button>
                    )}
                </Row>
            </Row>

            {bulkMode && (
                <div
                    role="note"
                    style={{
                        padding: '12px 16px',
                        borderRadius: 10,
                        fontSize: 13,
                        lineHeight: '20px',
                        backgroundColor: semanticColors.info.bg,
                        color: semanticColors.info.text,
                        border: `1px solid ${semanticColors.info.border}`,
                    }}
                >
                    <b>칸 고정 비밀번호</b>만 입력합니다. 회원의 개별 비밀번호는 건드리지 않습니다(「개별」 표시가 붙은 칸은
                    그 회원에게 고정값이 적용되지 않습니다).
                    <br />
                    {/*
                      * 그리드가 auto-fill 이라 열 수가 화면 폭에 따라 달라진다. 위·아래 방향키는
                      * "윗줄/아랫줄"이 아니라 **목록 순서상 이전/다음 칸**으로 움직인다.
                      * 문구를 실제 동작에 맞춘다.
                      */}
                    Enter 로 저장하고 다음 칸으로, 위·아래 방향키로 이전/다음 칸으로 이동합니다(좌우는 입력 커서용).
                    포커스된 칸만 비밀번호가 보입니다.
                    {(pendingCount > 0 || failedCount > 0) && (
                        <>
                            <br />
                            저장 대기 {pendingCount}칸 · 실패 {failedCount}칸 · 저장 완료 {savedCount}칸
                        </>
                    )}
                </div>
            )}

            {/* 검색 · 필터 */}
            <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="번호 · 회원명 · 연락처 검색"
                    aria-label="라커 검색"
                    style={{
                        flex: '1 1 260px',
                        minWidth: 220,
                        maxWidth: 360,
                        height: 36,
                        padding: '0 16px',
                        border: `1px solid ${colors.WHITE_600}`,
                        borderRadius: 999,
                        fontSize: 13,
                        outline: 'none',
                        color: colors.LAVEL_4,
                        boxSizing: 'border-box',
                    }}
                />
                <Row role="group" aria-label="상태 필터" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {LOCKER_STATUS_FILTERS.map((filter) => {
                        const active = statusFilter === filter.code;
                        return (
                            <button
                                key={filter.code}
                                type="button"
                                aria-pressed={active}
                                onClick={() => setStatusFilter(filter.code)}
                                style={{
                                    height: 32,
                                    padding: '0 12px',
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    border: `1px solid ${active ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
                                    backgroundColor: active ? colors.PRIMARY_BLUE : colors.white,
                                    color: active ? colors.white : colors.LAVEL_1,
                                }}
                            >
                                {filter.label}
                            </button>
                        );
                    })}
                </Row>
            </Row>

            {filtered.length === 0 ? (
                <LockerEmpty
                    icon="🔎"
                    title="조건에 맞는 라커가 없습니다"
                    description="검색어나 상태 필터를 조금 완화하면 더 많은 칸을 볼 수 있습니다."
                    actionLabel={hasSearchOrFilter ? '조건 초기화' : undefined}
                    onAction={
                        hasSearchOrFilter
                            ? () => {
                                  setSearchInput('');
                                  setStatusFilter('all');
                              }
                            : undefined
                    }
                />
            ) : (
                <>
                    <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>
                        {(safePage - 1) * ITEMS_PER_PAGE + 1}~{Math.min(safePage * ITEMS_PER_PAGE, filtered.length)}번째 ·
                        {` 총 ${filtered.length}칸`}
                    </span>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                            gap: 12,
                        }}
                    >
                        {pageItems.map((locker, index) => {
                            if (bulkMode) {
                                const draft = drafts[locker.number];
                                const value = draft !== undefined ? draft : baselineOf(locker);
                                const isActive =
                                    activeBulkNumber === null ? index === 0 : activeBulkNumber === locker.number;
                                return (
                                    <LockerBulkCell
                                        key={locker.number}
                                        locker={locker}
                                        value={value}
                                        state={cellStates[locker.number] ?? 'idle'}
                                        errorMessage={cellErrors[locker.number]}
                                        revealAll={revealAll}
                                        isFocused={focusedBulkNumber === locker.number}
                                        tabIndex={isActive ? 0 : -1}
                                        onChange={handleBulkChange}
                                        onCommit={handleBulkCommit}
                                        onFocus={handleBulkFocus}
                                        onBlur={handleBulkBlur}
                                        onMove={handleBulkMove}
                                    />
                                );
                            }
                            return (
                                <LockerCard
                                    key={locker.number}
                                    locker={locker}
                                    isSelected={selectedNumber === locker.number}
                                    revealAll={revealAll}
                                    readOnly={readOnly}
                                    calculateDday={calculateDday}
                                    onToggleSelect={handleToggleSelect}
                                    onCloseMenu={handleCloseMenu}
                                    onAssign={handleAssign}
                                    onEditPassword={handleEditPassword}
                                    onRelease={handleRelease}
                                    onOpenMember={handleOpenMember}
                                />
                            );
                        })}
                    </div>

                    {totalPages > 1 && (
                        <Row style={{ justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
                            <button
                                type="button"
                                disabled={safePage === 1}
                                onClick={() => setPage(safePage - 1)}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: `1px solid ${colors.WHITE_600}`,
                                    backgroundColor: colors.white,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: colors.LAVEL_1,
                                    cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                                    opacity: safePage === 1 ? 0.4 : 1,
                                }}
                            >
                                이전
                            </button>
                            <span style={{ fontSize: 13, color: colors.LAVEL_4, fontWeight: 700 }}>{safePage}</span>
                            <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>/ {totalPages}</span>
                            <button
                                type="button"
                                disabled={safePage === totalPages}
                                onClick={() => setPage(safePage + 1)}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: `1px solid ${colors.WHITE_600}`,
                                    backgroundColor: colors.white,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: colors.LAVEL_1,
                                    cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                                    opacity: safePage === totalPages ? 0.4 : 1,
                                }}
                            >
                                다음
                            </button>
                        </Row>
                    )}
                </>
            )}

            {/* 메뉴 바깥 클릭으로 닫기 */}
            {selectedLocker && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 15 }} onClick={handleCloseMenu} aria-hidden="true" />
            )}

            {assignModalNumber !== null && zoneId && (
                <LockerAssignModal
                    isVisible
                    onClose={() => closeModalAndRefocus(assignModalNumber, () => setAssignModalNumber(null))}
                    centerId={centerId}
                    zoneId={zoneId}
                    lockerNumber={assignModalNumber}
                    defaultLockNumber={
                        lockers.find((locker) => locker.number === assignModalNumber)?.defaultLockNumber ?? null
                    }
                    createAlert={createAlert}
                />
            )}

            {passwordModalLocker !== null && zoneId && (
                <LockerSlotPasswordModal
                    centerId={centerId}
                    zoneId={zoneId}
                    lockerNumber={passwordModalLocker}
                    onClose={() => closeModalAndRefocus(passwordModalLocker, () => setPasswordModalNumber(null))}
                    createAlert={createAlert}
                    createTwoButtonAlert={createTwoButtonAlert}
                />
            )}
        </Col>
    );
}
